import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
} from "react-bootstrap";
import CustomPagination from "../../../../../common/Pagination";
import TableRowsPerPage from "../../../../../common/TableRowsPerPage";
import { BILLING_STANDARD_CHARGES_DASHBOARD_HEADERS } from "../../actions/constant";
import BillingDataTable from "../BillingDataTable";
import AddUpdateStandardCharges from "./AddUpdateStandardCharges";
import { getBillingStandardChargesSearchList } from "../../../../../services/BillingAPI";
import Loader from "../../../../../common/Loader";
import { STANDARD_CHARGES_SORT_KEY_MAP } from "../../../Teams/action/constant";

const StandardChargesDashboard = ({
  triggerAdd,
  setTriggerAdd,
  activeTab,
}: any) => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [action, setAction] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [rowsData, setRowsData] = useState<any[]>([]);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const lastDepsRef = useRef<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [totalRows, setTotalRows] = useState<any>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(15);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<string>("DESC");

  const resolveStandardChargesSortColumn = (uiKey: string) => {
    return STANDARD_CHARGES_SORT_KEY_MAP[uiKey] ?? "";
  };

  const fetchStandardCharges = async (
    query: string = "",
    pageNo: number = 1,
    pageSize: number = itemsPerPage,
    key: string = sortColumn,
    direction: string = sortOrder
  ) => {
    try {
      setLoading(true);

      const trimmedQuery = query?.trim();
      const payload = {
        searchString: trimmedQuery || "",
        pageNo,
        pageSize,
        sortColumn: key,
        sortOrder: direction,
      };

      const response = await getBillingStandardChargesSearchList(payload);
      setRowsData(response || []);
      setTotalPages(response[0]?.totalPages || 1);
      setTotalRows(response[0]?.totalRecords || null);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== "Standard Charges") return;

    const currentDeps = JSON.stringify({
      searchQuery,
      currentPage,
      itemsPerPage,
      sortColumn,
      sortOrder,
    });

    if (lastDepsRef.current === currentDeps) return;
    lastDepsRef.current = currentDeps;

    fetchStandardCharges(
      searchQuery,
      currentPage,
      itemsPerPage,
      sortColumn,
      sortOrder
    );
  }, [
    activeTab,
    searchQuery,
    currentPage,
    itemsPerPage,
    sortColumn,
    sortOrder,
  ]);

  useEffect(() => {
    if (triggerAdd && activeTab === "Standard Charges") {
      setOpenDrawer(true);
    }
  }, [triggerAdd]);

  const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const handleInputChange = debounce((value: string) => {
    if (value.length >= 3 || value.length === 0) {
      setSearchQuery(value);
      setCurrentPage(1);
    }
  }, 300);

  const handleResetFilters = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    setSearchQuery("");
  };

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    fetchStandardCharges(
      searchQuery,
      pageNumber,
      itemsPerPage,
      sortColumn,
      sortOrder
    );
  };

  const handleSortAction = (row: any) => {
    const apiSortColumn = resolveStandardChargesSortColumn(row.key);
    setSortColumn(apiSortColumn);
    setSortOrder(row.direction);
    fetchStandardCharges(
      searchQuery,
      currentPage,
      itemsPerPage,
      apiSortColumn,
      row.direction
    );
  };

  const handleRowsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleRowAction = (row: any, action: string) => {
    setAction(action);
    setSelectedRowData(row);
    setOpenDrawer(true);
    setTriggerAdd(1);
  };

  const handleClose = () => {
    setOpenDrawer(false);
    setTriggerAdd(0);
    setSelectedRowData(null);
    fetchStandardCharges();
  };

  const entriesText = !totalRows
    ? "Showing 0 of 0 entries"
    : `Showing ${(currentPage - 1) * itemsPerPage + 1} to ${Math.min(
        currentPage * itemsPerPage,
        totalRows
      )} of ${totalRows} entries`;

  return (
    <div>
      {!openDrawer ? (
        <div className="main_wrapper_section appoinment_wrapper mt-1 minusTopBilling">
          <Container fluid>
            {/* <Loader isLoading={loading} /> */}
            <div className="container-fluid">
              <div className="mt-2 mb-2">
                <Row className="align-items-center justify-content-between mb-3">
                  <Col xs="auto" className="sectionTitle noborder pe-3">
                    Standard Charges List
                  </Col>
                  <Col xs="auto">
                    <div className="d-flex align-items-center gap-2">
                      <InputGroup className="w-auto">
                        <InputGroup.Text className="px-2">
                          <span className="material-symbols-outlined text-black-50">
                            search
                          </span>
                        </InputGroup.Text>
                        <Form.Control
                          ref={inputRef}
                          aria-label="Search"
                          onChange={(e) =>
                            handleInputChange(e.target.value.trim())
                          }
                          onPaste={(e) => {
                            e.preventDefault();
                            const text = e.clipboardData.getData("text").trim();
                            handleInputChange(text);
                            if (inputRef.current) inputRef.current.value = text;
                          }}
                        />
                      </InputGroup>
                      <Button
                        className="d-flex align-items-center"
                        style={{
                          background: "none",
                          border: "none",

                          padding: "10px",
                          fontSize: "22px",
                        }}
                        onClick={handleResetFilters}
                      >
                        <span className="material-icons reset_ic0">
                          restart_alt
                        </span>
                      </Button>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
            <div className="mb-3 container-fluid px-2 adjust_heightBilling">
              <BillingDataTable
                headers={BILLING_STANDARD_CHARGES_DASHBOARD_HEADERS}
                rows={rowsData}
                onSortAction={handleSortAction}
                onAction={handleRowAction}
                isStandardBilling={true}
              />
            </div>
            <div className="d-flex justify-content-between align-items-center mt-0 mb-4 row p-0 m-0">
              <div className="col-md-12">
                <div className="row">
                  <div className="col-md-6 showingData_wrapper">
                    <span>{entriesText}</span>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex flex-row-reverse ">
                      <CustomPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={paginate}
                      />
                      <TableRowsPerPage
                        value={itemsPerPage}
                        onSelect={handleRowsPerPageChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </div>
      ) : null}
      {openDrawer && activeTab === "Standard Charges" && (
        <AddUpdateStandardCharges onClose={handleClose} selectedRowData={selectedRowData}/>
      )}
    </div>
  );
};

export default StandardChargesDashboard;
